import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { cn } from '../../lib/utils';
import { useAppMode } from '../../contexts/AppModeContext';
import { useAuth } from '../../contexts/AuthContext';
import { capabilitiesFor } from '../../lib/permissions';
import * as userAccessApi from '../../lib/api/userAccessApi';
import { APP_ROLES, roleLabelFor } from '../../lib/api/userAccessApi';
import {
  ShieldCheck, ChevronDown, ChevronRight, Plus, Loader2, AlertTriangle,
  RefreshCw, Power, PowerOff, UserCog, Mail,
} from 'lucide-react';
import { toast } from 'sonner';

const DEMO_MAPPINGS = [
  { id: 'demo-ua-1', email: 'chen@valleymed.com',    app_role: 'super_admin', status: 'active',   auth_user_id: 'u-chen',    bootstrap_source: 'bootstrap_env', created_at: '2026-04-01T10:00:00Z' },
  { id: 'demo-ua-2', email: 'wilson@valleymed.com',  app_role: 'admin',       status: 'active',   auth_user_id: 'u-wilson',  bootstrap_source: 'invite',        created_at: '2026-04-02T10:00:00Z' },
  { id: 'demo-ua-3', email: 'kim@valleymed.com',     app_role: 'front_desk',  status: 'active',   auth_user_id: 'u-kim',     bootstrap_source: 'invite',        created_at: '2026-04-03T10:00:00Z' },
  { id: 'demo-ua-4', email: 'roberts@valleymed.com', app_role: 'provider',    status: 'active',   auth_user_id: 'u-roberts', bootstrap_source: 'default',       created_at: '2026-04-04T10:00:00Z' },
  { id: 'demo-ua-5', email: 'guest@valleymed.com',   app_role: 'read_only',   status: 'inactive', auth_user_id: 'u-guest',   bootstrap_source: 'default',       created_at: '2026-04-05T10:00:00Z' },
];

const statusTone = (s) => s === 'active'
  ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
  : 'bg-slate-100 text-slate-500 border-slate-200';

const roleTone = (r) => {
  if (r === 'super_admin' || r === 'admin') return 'bg-indigo-50 text-indigo-700 border-indigo-100';
  if (r === 'front_desk')                   return 'bg-amber-50 text-amber-700 border-amber-100';
  if (r === 'provider')                     return 'bg-blue-50 text-blue-700 border-blue-100';
  return 'bg-slate-100 text-slate-600 border-slate-200';
};

const UserAccessPanel = () => {
  const { isDemo } = useAppMode();
  const { user } = useAuth();
  const { canTeamAdmin } = capabilitiesFor(user);
  const allowAdmin = isDemo || canTeamAdmin;

  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(isDemo ? DEMO_MAPPINGS : []);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('front_desk');
  const [inviteSaving, setInviteSaving] = useState(false);

  const fetchRows = useCallback(async () => {
    if (isDemo) { setRows(DEMO_MAPPINGS); setLoading(false); setLoadError(null); return; }
    if (!allowAdmin) { setRows([]); setLoading(false); return; }
    setLoading(true); setLoadError(null);
    const res = await userAccessApi.listMappings();
    if (res.ok) {
      setRows(res.data?.users || []);
    } else {
      setLoadError(res.error);
    }
    setLoading(false);
  }, [isDemo, allowAdmin]);

  useEffect(() => { if (open) fetchRows(); }, [open, fetchRows]);

  const handleRoleChange = async (row, nextRole) => {
    if (row.app_role === nextRole) return;
    if (isDemo) {
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, app_role: nextRole } : r)));
      toast.success(`Role updated to ${roleLabelFor(nextRole)} (demo)`);
      return;
    }
    setBusyId(row.id);
    const res = await userAccessApi.setRole(row.id, nextRole);
    setBusyId(null);
    if (res.ok) {
      toast.success(`Role updated to ${roleLabelFor(nextRole)}`);
      fetchRows();
    } else {
      toast.error('Role update failed', { description: res.error?.message });
    }
  };

  const handleToggleStatus = async (row) => {
    const next = row.status === 'active' ? 'inactive' : 'active';
    if (isDemo) {
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: next } : r)));
      toast.success(`${row.email} ${next === 'active' ? 'reactivated' : 'deactivated'} (demo)`);
      return;
    }
    setBusyId(row.id);
    const res = await userAccessApi.setStatus(row.id, next);
    setBusyId(null);
    if (res.ok) {
      toast.success(`${row.email} ${next === 'active' ? 'reactivated' : 'deactivated'}`);
      fetchRows();
    } else {
      toast.error('Status change failed', { description: res.error?.message });
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail || !inviteRole) {
      toast.error('Email and role are required');
      return;
    }
    if (isDemo) {
      const id = `demo-ua-${rows.length + 1}`;
      setRows((prev) => [
        {
          id, email: inviteEmail, app_role: inviteRole, status: 'active',
          auth_user_id: `pending:${inviteEmail}`, bootstrap_source: 'invite',
          created_at: new Date().toISOString(),
        },
        ...prev,
      ]);
      toast.success(`${inviteEmail} invited as ${roleLabelFor(inviteRole)} (demo)`);
      setInviteEmail(''); setInviteRole('front_desk'); setInviteOpen(false);
      return;
    }
    setInviteSaving(true);
    const res = await userAccessApi.inviteUser({ email: inviteEmail, app_role: inviteRole });
    setInviteSaving(false);
    if (res.ok) {
      toast.success(`${inviteEmail} invited as ${roleLabelFor(inviteRole)}`);
      setInviteEmail(''); setInviteRole('front_desk'); setInviteOpen(false);
      fetchRows();
    } else {
      toast.error('Invite failed', { description: res.error?.message });
    }
  };

  // Unmapped authenticated users (real mode, not admin) get a compact read-only banner only.
  if (!isDemo && !allowAdmin) {
    return (
      <Card className="border-amber-100 bg-amber-50/40" data-testid="user-access-readonly-banner">
        <CardContent className="p-3 flex items-center gap-3">
          <ShieldCheck className="h-4 w-4 text-amber-600 shrink-0" />
          <div className="flex-1">
            <p className="text-xs font-semibold text-amber-800">You have read-only access</p>
            <p className="text-[10px] text-amber-700">
              Your account isn't mapped to an admin role for this organization yet.
              Ask a Super Admin to grant access via the User Access panel.
            </p>
          </div>
          {user?.app_role && (
            <Badge variant="outline" className={cn('text-[9px]', roleTone(user.app_role))}>
              {roleLabelFor(user.app_role)}
            </Badge>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200" data-testid="user-access-panel">
      <CardContent className="p-0">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center gap-2 px-4 py-3 hover:bg-slate-50 rounded-t-lg"
          style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}
          data-testid="user-access-toggle"
        >
          {open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
          <ShieldCheck className="h-4 w-4 text-indigo-500" />
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold text-slate-800">User Access &amp; Roles</p>
            <p className="text-[10px] text-slate-400">
              Access role (permissions). Separate from a member&apos;s staff role in Teams. Admin-only.
            </p>
          </div>
          <Badge className="text-[9px] border-0 bg-indigo-50 text-indigo-700">
            {rows.length} mapped
          </Badge>
        </button>

        {open && (
          <div className="border-t border-slate-100 p-4 space-y-3" data-testid="user-access-body">
            <div className="flex items-center justify-between">
              <p className="text-[10px] text-slate-500 uppercase tracking-wide font-semibold">
                Mapped Users
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm" variant="outline" className="text-xs gap-1 h-8"
                  onClick={fetchRows}
                  data-testid="user-access-refresh-btn"
                >
                  <RefreshCw className="h-3 w-3" /> Refresh
                </Button>
                <Button
                  size="sm" className="text-xs gap-1 h-8 bg-[#111] hover:bg-[#222]"
                  onClick={() => setInviteOpen(true)}
                  data-testid="user-access-invite-btn"
                >
                  <Plus className="h-3 w-3" /> Invite
                </Button>
              </div>
            </div>

            {loading && (
              <div className="flex items-center justify-center py-6" data-testid="user-access-loading">
                <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                <span className="ml-2 text-xs text-slate-500">Loading mappings…</span>
              </div>
            )}

            {!loading && loadError && (
              <div className="flex items-center gap-2 p-3 rounded-md bg-red-50 border border-red-100" data-testid="user-access-load-error">
                <AlertTriangle className="h-4 w-4 text-red-500" />
                <p className="text-xs text-red-700 flex-1">{loadError.message || 'Request failed'}</p>
                <Button size="sm" variant="outline" className="text-xs h-7" onClick={fetchRows}>
                  Retry
                </Button>
              </div>
            )}

            {!loading && !loadError && rows.length === 0 && (
              <div className="py-8 text-center" data-testid="user-access-empty">
                <UserCog className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                <p className="text-xs font-medium text-slate-600">No user mappings yet</p>
                <p className="text-[10px] text-slate-400 mt-1">
                  Invite your first user or log in with a bootstrap admin email.
                </p>
              </div>
            )}

            {!loading && !loadError && rows.length > 0 && (
              <div className="overflow-x-auto rounded-md border border-slate-100">
                <table className="w-full text-xs" data-testid="user-access-table">
                  <thead className="bg-slate-50 text-[10px] text-slate-500 uppercase">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Email</th>
                      <th className="text-left px-3 py-2 font-semibold">Role</th>
                      <th className="text-left px-3 py-2 font-semibold">Status</th>
                      <th className="text-left px-3 py-2 font-semibold">Source</th>
                      <th className="text-right px-3 py-2 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id} className="border-t border-slate-100" data-testid={`ua-row-${row.id}`}>
                        <td className="px-3 py-2">
                          <div className="font-medium text-slate-800 truncate max-w-[220px]">{row.email}</div>
                          <div className="text-[10px] text-slate-400 truncate max-w-[220px]">{row.auth_user_id}</div>
                        </td>
                        <td className="px-3 py-2">
                          <select
                            value={row.app_role}
                            onChange={(e) => handleRoleChange(row, e.target.value)}
                            disabled={busyId === row.id}
                            className={cn(
                              'px-2 py-1 rounded border text-[11px] font-medium outline-none',
                              roleTone(row.app_role),
                            )}
                            data-testid={`ua-role-select-${row.id}`}
                          >
                            {APP_ROLES.map((r) => (
                              <option key={r.id} value={r.id}>{r.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className={cn('text-[10px]', statusTone(row.status))}>
                            {row.status}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-[10px] text-slate-500">{row.bootstrap_source || '—'}</td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            size="sm" variant="outline"
                            className="text-[10px] h-7 gap-1"
                            onClick={() => handleToggleStatus(row)}
                            disabled={busyId === row.id}
                            data-testid={`ua-toggle-${row.id}`}
                          >
                            {busyId === row.id
                              ? <Loader2 className="h-3 w-3 animate-spin" />
                              : row.status === 'active'
                                ? <PowerOff className="h-3 w-3 text-red-500" />
                                : <Power className="h-3 w-3 text-emerald-600" />}
                            {row.status === 'active' ? 'Deactivate' : 'Reactivate'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ═══ INVITE DIALOG ═══ */}
        <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
          <DialogContent className="sm:max-w-md" data-testid="user-access-invite-dialog">
            <DialogHeader>
              <DialogTitle className="text-base">Invite User to Access Role</DialogTitle>
              <DialogDescription className="text-xs">
                Pre-provision a role for this email. It will take effect the first time they log in.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <p className="text-[10px] text-slate-500 mb-1">Email *</p>
                <input
                  type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="user@clinic.com"
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-xs outline-none focus:border-slate-400"
                  data-testid="ua-invite-email"
                />
              </div>
              <div>
                <p className="text-[10px] text-slate-500 mb-1">Access Role *</p>
                <div className="flex flex-wrap gap-1.5">
                  {APP_ROLES.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => setInviteRole(r.id)}
                      className={cn(
                        'px-2.5 py-1.5 rounded-md text-[11px] font-medium transition-colors',
                        inviteRole === r.id ? 'bg-[#111] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
                      )}
                      style={{ border: 'none', cursor: 'pointer' }}
                      data-testid={`ua-invite-role-${r.id}`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
                {inviteRole && (
                  <p className="text-[10px] text-slate-400 mt-1.5">
                    {(APP_ROLES.find((r) => r.id === inviteRole) || {}).desc}
                  </p>
                )}
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" size="sm" className="text-xs" onClick={() => setInviteOpen(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                className="text-xs gap-1 bg-[#111] hover:bg-[#222]"
                onClick={handleInvite}
                disabled={inviteSaving}
                data-testid="ua-invite-confirm"
              >
                {inviteSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}
                {inviteSaving ? 'Inviting…' : 'Send Invite'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default UserAccessPanel;
